var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import React, { useEffect, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom';
import { colors } from '../../Themes/colors';
import { utility } from '../../utils';
import constants from '../../config/constants';
var isProduction = process.env.NODE_ENV === 'production';
var getErrorMessage = function (error) {
    if (isRouteErrorResponse(error)) {
        return "".concat(error.status, " ").concat(error.statusText).concat(error.data ? " \u2014 ".concat(JSON.stringify(error.data)) : '');
    }
    if (error instanceof Error) {
        return error.message;
    }
    if (typeof error === 'string') {
        return error;
    }
    try {
        return JSON.stringify(error);
    }
    catch (_a) {
        return String(error);
    }
};
var RouteErrorBoundary = function () {
    var _a;
    var error = useRouteError();
    var navigate = useNavigate();
    var _b = useState(false), showDetails = _b[0], setShowDetails = _b[1];
    var message = getErrorMessage(error);
    var stack = error instanceof Error ? error.stack : undefined;
    var timestamp = new Date().toISOString();
    var errorReport = [
        "Time: ".concat(timestamp),
        "URL: ".concat(window.location.href),
        "Message: ".concat(message),
        stack ? "Stack:\n".concat(stack) : null,
    ].filter(Boolean).join('\n');
    useEffect(function () {
        console.error('[RouteErrorBoundary] Unhandled route error:', { message: message, stack: stack, url: window.location.href, timestamp: timestamp, error: error });
    }, [error]);
    var handleCopy = function () { return __awaiter(void 0, void 0, void 0, function () {
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    _b.trys.push([0, 2, , 3]);
                    return [4, navigator.clipboard.writeText(errorReport)];
                case 1:
                    _b.sent();
                    utility.alert('Error details copied to clipboard', constants.TOAST_TYPES.INFO);
                    return [3, 3];
                case 2:
                    _a = _b.sent();
                    utility.alert('Unable to copy error details', constants.TOAST_TYPES.ERROR);
                    return [3, 3];
                case 3: return [2];
            }
        });
    }); };
    return (React.createElement(Box, { sx: {
            minHeight: '100dvh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            px: 3,
            py: 4,
            backgroundColor: colors.greyShades[11],
        } },
        React.createElement(Stack, { spacing: 2, alignItems: "center", maxWidth: 640 },
            React.createElement(Typography, { variant: "h5", fontWeight: 700, color: colors.blueShades[12] }, "Something went wrong"),
            React.createElement(Typography, { variant: "body1", color: colors.greyShades[15] }, "An unexpected error occurred while loading this page. Please try again."),
            React.createElement(Box, { sx: {
                    width: '100%',
                    backgroundColor: (_a = colors.fullWhite) !== null && _a !== void 0 ? _a : '#FFFFFF',
                    border: "1px solid ".concat(colors.greyShades[12]),
                    borderRadius: 1,
                    p: 1.5,
                    textAlign: 'left',
                } },
                React.createElement(Typography, { component: "pre", sx: {
                        m: 0,
                        fontFamily: 'monospace',
                        fontSize: 13,
                        color: colors.errorShades[10],
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                    } }, message)),
            !isProduction && stack && (React.createElement(Box, { sx: { width: '100%', textAlign: 'left' } },
                React.createElement(Button, { size: "small", onClick: function () { return setShowDetails(function (prev) { return !prev; }); } },
                    showDetails ? 'Hide' : 'Show',
                    " technical details"),
                showDetails && (React.createElement(Box, { sx: {
                        mt: 1,
                        maxHeight: 240,
                        overflow: 'auto',
                        backgroundColor: colors.greyShades[14],
                        borderRadius: 1,
                        p: 1.5,
                    } },
                    React.createElement(Typography, { component: "pre", sx: { m: 0, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' } }, stack))))),
            React.createElement(Stack, { direction: "row", spacing: 2, pt: 1, flexWrap: "wrap", justifyContent: "center" },
                React.createElement(Button, { variant: "outlined", onClick: function () { return window.location.reload(); } }, "Reload Page"),
                React.createElement(Button, { variant: "contained", onClick: function () { return navigate('/'); } }, "Go to Dashboard"),
                React.createElement(Button, { variant: "text", onClick: handleCopy }, "Copy Error Details")))));
};
export default RouteErrorBoundary;
